// src/components/FinalSettlementModal.tsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  fetchEmployeesForSettlement,
  saveSettlement,
} from "../../store/slices/payrollSlice";
import { EmployeeDetails } from "../../models/employeeDetails.model";
import {
  fetchLeaveBalance,
  fetchUserLeaveBalance,
} from "../../store/slices/leaveSlice";
import toast from "react-hot-toast";

interface FinalSettlementModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingData?: any;
}

const FinalSettlementModal: React.FC<FinalSettlementModalProps> = ({
  isOpen,
  onClose,
  editingData,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeDetails | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState("employee");
  const [showEmployeeList, setShowEmployeeList] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const leaveBalances = useSelector((state: RootState) => state.leave.balances);

  useEffect(() => {
    if (leaveBalances && !isEditing && !selectedEmployee) {
      setSettlementData((prev) => ({
        ...prev,
        leaveEncashment: [
          { type: "pl", balance: leaveBalances.paid || 0, encashed: 0 },
          { type: "cof", balance: leaveBalances.compOff || 0, encashed: 0 },
        ],
      }));
    }
  }, [leaveBalances, isEditing, selectedEmployee]);

  const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return "";
    const d = new Date(date);
    return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0]; // YYYY-MM-DD
  };

const emptySettlementData = {
  employeeId: "",
  resignationSubmittedOn: "",
  leavingDate: "",
  leavingReason: "",
  settlementDate: "",
  noticeRequired: false,
  noticePeriod: 0,
  daysServed: 0,
  payrollMonth: "",
  workDays: 0,
  daysWorked: 0,
  leaveEncashment: [
    { type: "cof", balance: 0, encashed: 0 },
    { type: "pl", balance: 0, encashed: 0 },
  ],
};

  const [settlementData, setSettlementData] = useState(emptySettlementData);
  const { settlementEmployees, settlementEmployeesList } = useSelector(
    (state: RootState) => state.payroll
  );
  useEffect(() => {
    dispatch(fetchEmployeesForSettlement());
  }, [dispatch]);

  useEffect(() => {
    // Show employee list when input is focused
    if (isInputFocused && !selectedEmployee) {
      setShowEmployeeList(true);
    }
  }, [isInputFocused, selectedEmployee]);

  const handleEmployeeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowEmployeeList(true);
  };

  const handleEmployeeSelect = async (employee: EmployeeDetails) => {
    // Find the full employee object from the slice
    const fullEmployee = settlementEmployees.find(
      (emp) => emp.userId === employee.userId
    );

    if (!fullEmployee) return;

    setSelectedEmployee(fullEmployee);
    const result = await dispatch(
      fetchUserLeaveBalance(fullEmployee.userId)
    ).unwrap();
    const leaveBalances = {
      pl: result?.paid || 0,
      cof: result?.compOff || 0,
    };
    // Populate settlementData for all steps
    setSettlementData({
      employeeId: fullEmployee.userId,
      resignationSubmittedOn: formatDate(fullEmployee.resignationSubmittedOn),
      leavingDate: formatDate(fullEmployee.leavingDate),
      leavingReason: fullEmployee.reasonForLeaving || "",
      settlementDate: formatDate(fullEmployee.settledOn),
      noticeRequired: fullEmployee.noticeRequired || false,
      noticePeriod: fullEmployee.noticePeriod || 0,
      daysServed:
        (fullEmployee.noticePeriod ?? 0) -
        (fullEmployee.shortFallInNoticePeriod ?? 0),
      payrollMonth: fullEmployee.payrollMonth || "",
      workDays: fullEmployee.workDays ?? 0,
      daysWorked: fullEmployee.daysWorked ?? 0,
      leaveEncashment: [
        { type: "pl", balance: leaveBalances?.pl || 0, encashed: 0 },
        { type: "cof", balance: leaveBalances?.cof || 0, encashed: 0 },
      ],
    });

    // Update search input and UI
    setSearchTerm(fullEmployee.aadharName || fullEmployee.employeeNo || "");
    setShowEmployeeList(false);
    setIsInputFocused(false);
  };
  const handleInputFocus = () => {
    setIsInputFocused(true);
    if (!selectedEmployee) {
      setShowEmployeeList(true);
    }
  };

  const handleInputBlur = () => {
    // Use setTimeout to allow click event to register before hiding the list
    setTimeout(() => {
      setIsInputFocused(false);
      setShowEmployeeList(false);
    }, 200);
  };

  const handleCancelSelection = () => {
    setSelectedEmployee(null);
    setSearchTerm("");
    setShowEmployeeList(true);
    setSettlementData(emptySettlementData);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    // Convert numeric fields to numbers
    const numericFields = [
      "noticePeriod",
      "daysServed",
      "workDays",
      "daysWorked",
    ];

    if (numericFields.includes(name)) {
      setSettlementData({
        ...settlementData,
        [name]: value === "" ? 0 : Number(value),
      });
    } else {
      setSettlementData({
        ...settlementData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleLeaveEncashmentChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updatedLeaveEncashment = [...settlementData.leaveEncashment];

    // Convert to number for balance and encashed fields
    const processedValue =
      field === "balance" || field === "encashed"
        ? value === ""
          ? 0
          : Number(value)
        : value;

    updatedLeaveEncashment[index] = {
      ...updatedLeaveEncashment[index],
      [field]: processedValue,
    };

    setSettlementData({
      ...settlementData,
      leaveEncashment: updatedLeaveEncashment,
    });
  };

const handleSubmit = async () => {
  const settlementDate = settlementData.settlementDate
    ? new Date(settlementData.settlementDate)
    : new Date();
  const year = settlementData.payrollMonth
    ? parseInt(settlementData.payrollMonth.split("-")[0])
    : settlementDate.getFullYear();
  const month = settlementData.payrollMonth
    ? parseInt(settlementData.payrollMonth.split("-")[1])
    : settlementDate.getMonth() + 1;
  const payload = {
    ...settlementData,
    year,
    payrollMonth: month,
    leaveEncashment: settlementData.leaveEncashment.map((l: any) => ({
      type: l.type,
      balance: Number(l.balance) || 0,
      encashed: Number(l.encashed) || 0,
    })),
  };
  try {
    const result = await dispatch(saveSettlement(payload)).unwrap();
    toast.success("Settlement saved successfully");
    onClose();
  } catch (error: any) {
    toast.error(error?.message || "Failed to save settlement");
  }
};

useEffect(() => {
  if (editingData) {
    setIsEditing(true);
    const emp = settlementEmployees.find(
      (emp) => emp.userId === editingData.userId
    );

    setSelectedEmployee(emp || null);
    setSearchTerm(emp?.aadharName || editingData.employeeName || "N/A");

    // Derive year from settlementDate or current year
    const year = editingData.settlementDate
      ? new Date(editingData.settlementDate).getFullYear()
      : new Date().getFullYear();
    const payrollMonth = editingData.payrollMonth
      ? `${year}-${String(editingData.payrollMonth).padStart(2, "0")}`
      : "";

    const newSettlementData = {
      employeeId: editingData.userId || "",
      resignationSubmittedOn: editingData.resignationSubmittedOn || "",
      leavingDate: editingData.leavingDate || "",
      leavingReason: editingData.leavingReason || "",
      settlementDate: editingData.settlementDate || "",
      noticeRequired: editingData.noticeRequired || false,
      noticePeriod: Number(editingData.noticePeriod) || 0,
      daysServed: Number(editingData.daysServed) || 0,
      payrollMonth, // Store as YYYY-MM string
      workDays: Number(editingData.workDays) || 0,
      daysWorked: Number(editingData.daysWorked) || 0,
      leaveEncashment: Array.isArray(editingData.leaveEncashment) && editingData.leaveEncashment.length
        ? editingData.leaveEncashment.map((item: any) => ({
            type: item.type || "",
            balance: Number(item.balance) || 0,
            encashed: Number(item.encashed) || 0,
          }))
        : [
            { type: "cof", balance: 0, encashed: 0 },
            { type: "pl", balance: 0, encashed: 0 },
          ],
    };
    setSettlementData(newSettlementData);
  } else {
    setIsEditing(false);
    setSelectedEmployee(null);
    setSearchTerm("");
    setSettlementData(emptySettlementData);
  }
}, [editingData, settlementEmployees]);


  const filteredEmployees = (settlementEmployeesList || []).filter((emp) =>
    (emp.aadharName || emp.employeeNo || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Header */}
        <div className="sticky top-0 bg-sky-100 text-black p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold">
                {isEditing ? "Edit Settlement" : "Settle Employee"}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors duration-200"
            >
              <svg
                className="w-7 h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Tabs - Centered */}
          <div className="flex mt-6 space-x-1 justify-center">
            {["employee", "resignation", "notice", "work", "leave"].map(
              (section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`px-4 py-2 rounded-t-md text-sm font-medium transition-colors duration-200 ${
                    activeSection === section
                      ? "bg-white text-navyBlue-primary"
                      : "bg-navyBlue-primary text-blue-100 hover:bg-blue-600"
                  }`}
                >
                  {section === "employee" && "Employee"}
                  {section === "resignation" && "Resignation"}
                  {section === "notice" && "Notice Pay"}
                  {section === "work" && "Work Days"}
                  {section === "leave" && "Leave Encashment"}
                </button>
              )
            )}
          </div>
        </div>

        <div className="p-6">
          {isEditing && activeSection === "employee" && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-800">
                  Employee Details
                </h4>
                <span className="text-sm text-navyBlue-primary bg-blue-50 px-3 py-1 rounded-full">
                  Step 1 of 5
                </span>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900">
                  Name: {editingData.employeeName || "N/A"}
                </p>
                <p className="text-sm text-gray-700">
                  ID: {editingData.employeeNo || "N/A"}
                </p>
              </div>
            </div>
          )}{" "}
          {/* Employee Selection - Only show for new settlements */}
          {!isEditing && activeSection === "employee" && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-800">
                  Employee Selection
                </h4>
                <span className="text-sm text-navyBlue-primary bg-blue-50 px-3 py-1 rounded-full">
                  Step 1 of 5
                </span>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Employee
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleEmployeeSearch}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by name or employee ID"
                  />
                </div>
              </div>

              {showEmployeeList && (
                <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                  {filteredEmployees.map((emp) => (
                    <div
                      key={emp.userId}
                      className={`p-3 border-b border-gray-100 cursor-pointer transition-colors duration-150 ${
                        selectedEmployee?.userId === emp.userId
                          ? "bg-blue-50 border-l-4 border-l-navyBlue-primary"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        const fullEmp = settlementEmployees.find(
                          (e) => e.userId === emp.userId
                        );
                        if (fullEmp) handleEmployeeSelect(fullEmp);
                      }}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-navyBlue-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {emp.aadharName?.charAt(0) || "Unknown"}
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900 text-sm">
                            {emp.aadharName}
                          </p>
                          <p className="text-xs text-gray-500">
                            ID: {emp.employeeNo}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredEmployees.length === 0 && (
                    <div className="p-3 text-center text-gray-500 bg-gray-50">
                      <svg
                        className="w-8 h-8 mx-auto text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="mt-1 text-sm">No employees found</p>
                    </div>
                  )}
                </div>
              )}

              {selectedEmployee && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <h3 className="text-xs font-medium text-green-800">
                        Employee Selected
                      </h3>
                      <div className="text-xs text-green-700">
                        <p>
                          {selectedEmployee.aadharName} (ID:{" "}
                          {selectedEmployee.employeeNo})
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* X button to cancel selection */}
                  <button
                    onClick={handleCancelSelection}
                    className="text-red-500 hover:text-red-700 ml-4"
                    title="Clear selection"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Resignation Details */}
          {activeSection === "resignation" && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-800">
                  Resignation Details
                </h4>
                <span className="text-sm text-navyBlue-primary bg-blue-50 px-3 py-1 rounded-full">
                  Step 2 of 5
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resignation Submitted On
                  </label>
                  <input
                    type="date"
                    name="resignationSubmittedOn"
                    value={formatDate(settlementData.resignationSubmittedOn)}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leaving Date
                  </label>
                  <input
                    type="date"
                    name="leavingDate"
                    value={formatDate(settlementData.leavingDate)}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leaving Reason
                  </label>
                  <textarea
                    name="leavingReason"
                    value={settlementData.leavingReason || ""}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                    placeholder="Provide reason for leaving"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Settlement Date
                  </label>
                  <input
                    type="date"
                    name="settlementDate"
                    value={formatDate(settlementData.settlementDate)}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}{" "}
          {/* Notice Pay */}
          {activeSection === "notice" && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-800">
                  Notice Pay
                </h4>
                <span className="text-sm text-navyBlue-primary bg-blue-50 px-3 py-1 rounded-full">
                  Step 3 of 5
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-center md:col-span-2">
                  <input
                    type="checkbox"
                    id="noticeRequired"
                    name="noticeRequired"
                    checked={settlementData.noticeRequired || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="noticeRequired"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    <span className="font-medium">Notice Required</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notice Period (Days)
                  </label>
                  <input
                    type="number"
                    name="noticePeriod"
                    value={settlementData.noticePeriod || 0}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter days"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Days Served
                  </label>
                  <input
                    type="number"
                    name="daysServed"
                    value={settlementData.daysServed || 0}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter days served"
                  />
                </div>
              </div>
            </div>
          )}{" "}
          {/* Work Days */}
{activeSection === "work" && (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-4">
      <h4 className="text-xl font-semibold text-gray-800">
        Work Days
      </h4>
      <span className="text-sm text-navyBlue-primary bg-blue-50 px-3 py-1 rounded-full">
        Step 4 of 5
      </span>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payroll Month
        </label>
        <input
          type="month"
          name="payrollMonth"
          value={settlementData.payrollMonth || ""}
          onChange={(e) => {
            setSettlementData({
              ...settlementData,
              payrollMonth: e.target.value || "",
            });
          }}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Work Days
        </label>
        <input
          type="number"
          name="workDays"
          value={settlementData.workDays || 0}
          onChange={handleInputChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Total work days"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Days Worked
        </label>
        <input
          type="number"
          name="daysWorked"
          value={settlementData.daysWorked || 0}
          onChange={handleInputChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Days worked"
        />
      </div>
    </div>
  </div>
)}          {/* Leave Encashment */}
          {activeSection === "leave" && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-800">
                  Leave Encashment
                </h4>
                <span className="text-sm text-navyBlue-primary bg-blue-50 px-3 py-1 rounded-full">
                  Step 5 of 5
                </span>
              </div>

              <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Leave Type
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Encashed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {settlementData.leaveEncashment.map((item, index) => (
                      <tr
                        key={item.type || index}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.type === "cof"
                            ? "Comp Off (COF)"
                            : item.type === "pl"
                            ? "Paid Leave (PL)"
                            : "Unknown"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            value={item.balance || 0}
                            onChange={(e) =>
                              handleLeaveEncashmentChange(
                                index,
                                "balance",
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            value={item.encashed || 0}
                            onChange={(e) =>
                              handleLeaveEncashmentChange(
                                index,
                                "encashed",
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}{" "}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <div>
              {activeSection !== "employee" && (
                <button
                  onClick={() => {
                    const sections = [
                      "employee",
                      "resignation",
                      "notice",
                      "work",
                      "leave",
                    ];
                    const currentIndex = sections.indexOf(activeSection);
                    setActiveSection(sections[currentIndex - 1]);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium text-sm"
                >
                  Previous
                </button>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium text-sm"
              >
                Cancel
              </button>

              {activeSection !== "leave" ? (
                <button
                  onClick={() => {
                    const sections = [
                      "employee",
                      "resignation",
                      "notice",
                      "work",
                      "leave",
                    ];
                    const currentIndex = sections.indexOf(activeSection);
                    setActiveSection(sections[currentIndex + 1]);
                  }}
                  disabled={
                    !isEditing &&
                    !selectedEmployee &&
                    activeSection === "employee"
                  }
                  className={`px-4 py-2 rounded-lg text-white font-medium text-sm transition-colors duration-200 ${
                    !isEditing &&
                    !selectedEmployee &&
                    activeSection === "employee"
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-navyBlue-primary hover:bg-blue-700"
                  }`}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isEditing && !selectedEmployee}
                  className={`px-4 py-2 rounded-lg text-white font-medium text-sm transition-colors duration-200 ${
                    !isEditing && !selectedEmployee
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {isEditing ? "Update Settlement" : "Create Settlement"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalSettlementModal;
